import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRole } from '../utils/getActiveRole';
import { hasFormAccess, hasStateFormLibrary } from '../utils/docusignFormAccess';
import { formatPlainDate, formatUtcNaiveInstantForViewer } from '../utils/dateTime';
import './TransactionsPage.css';
import listingIcon from '../assets/listing-icon.png';
import keyIcon from '../assets/key-icon.png';
import TransactionTemplatesView from '../components/transaction/TransactionTemplatesView';

const BROKER_ROLES = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];

// Server-paginated tabs — 9 per page fits a 3-column grid cleanly without
// scrolling past the fold on typical laptop viewports.
const PAGE_SIZE = 9;

const TransactionsPage = () => {
  const navigate = useNavigate();
  // Dev-only bypass for the DocuSign readiness gate below — stg/prod keep the full gate
  // exactly as it was; dev skips it (matches ConnectedServicesPage's isDevEnvironment gate).
  const isDevEnvironment = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'rialty-ai-dev';
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState(() => {
    if (location.state?.filter) return location.state.filter;
    try {
      const storedUserData = localStorage.getItem('user');
      const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
      const role = getActiveRole(parsedData);
      if (BROKER_ROLES.includes(role) || role === 'BROKERAGE') return 'active';
    } catch (e) {}
    return 'prospect';
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [isRoleReady, setIsRoleReady] = useState(false);
  const [linkedAgentIds, setLinkedAgentIds] = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchError, setFetchError] = useState(false);
  const dropdownRef = useRef(null);

  // DocuSign readiness gate — mirrors the same gate on DashboardPage before
  // opening the Listing/Buying transaction creation flow.
  const [isConnectingDocusign, setIsConnectingDocusign] = useState(false);
  const [dsModal, setDsModal] = useState(null); // 'not_connected' | 'pending' | 'no_forms' | null
  const [dsModalDestination, setDsModalDestination] = useState(null);
  const [dsModalRoomsUserId, setDsModalRoomsUserId] = useState(null);
  const [dsResendingInvite, setDsResendingInvite] = useState(false);
  const [dsResendSuccess, setDsResendSuccess] = useState(false);
  const [dsAddingFormProvider, setDsAddingFormProvider] = useState(false);
  const [dsChecking, setDsChecking] = useState(null); // destination currently being checked, or null

  // Detect user role from localStorage — always use the active/switched role
  useEffect(() => {
    try {
      const storedUserData = localStorage.getItem('user');
      const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
      setUserRole(getActiveRole(parsedData));
    } catch (error) {
      console.error('Error parsing user data:', error);
      setUserRole('');
    }
    setIsRoleReady(true);
  }, []);

  const isAgent = userRole === 'AGENT' || userRole === 'TRANSACTION_COORDINATOR';
  const isBroker = BROKER_ROLES.includes(userRole);
  const isBrokerage = userRole === 'BROKERAGE';
  // Combined flag for UI decisions shared between broker and brokerage (tabs, search, empty states)
  const isBrokerOrBrokerage = isBroker || isBrokerage;
  // Read fresh from localStorage (same pattern used inside handleCreateTransactionClick
  // below) rather than relying on component state that may not be populated yet.
  const _txnListAgentState = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
    } catch { return ''; }
  })();
  // DocuSign parity with Agent extends to Broker and TC.
  // CA is excluded: the pre-create readiness gate (this page's "DocuSign Not Connected"
  // popup) is not shown for CA agents. Document/envelope/form functionality elsewhere
  // is untouched by this and keeps working for CA as before.
  // DocuSign is COLORADO-ONLY — every other state (CA, TX, …) uses Lone Wolf, so this pre-create
  // readiness gate must only appear for CO agents. Was `!== 'CA'` (CA only), which wrongly showed
  // the DocuSign gate for TX (and any non-CO, non-CA state). `=== ''` fail-open for load timing.
  const canSeeDocuSign = (userRole === 'AGENT' || isBroker || userRole === 'TRANSACTION_COORDINATOR')
    && (_txnListAgentState === 'CO' || _txnListAgentState === '');

  // Brokerage, Broker, Agent and TC can all create templates (Brokerage/Broker ones are
  // inherited by their agents when creating a transaction).
  const canUseTemplates = isAgent || userRole === 'BROKER' || isBrokerage;
  // Transactions / Templates toggle lives in the URL (?view=templates) so refresh and Back keep it.
  const isTemplatesView = canUseTemplates && new URLSearchParams(location.search).get('view') === 'templates';
  const setView = (view) => {
    navigate(view === 'templates' ? '/transactions?view=templates' : '/transactions');
  };

  // Brokers and brokerage cannot see Prospect status — redirect to Active if they land on it
  useEffect(() => {
    if (isRoleReady && isBrokerOrBrokerage && activeFilter === 'prospect') {
      setActiveFilter('active');
    }
  }, [isRoleReady, isBrokerOrBrokerage]);

  // For brokers: fetch linked agent IDs so transactions can be scoped correctly
  useEffect(() => {
    if (!isRoleReady) return;
    if (!isBroker) {
      setAgentsLoaded(true);
      return;
    }
    const load = async () => {
      try {
        const token = await getFirebaseToken();
        if (userRole === 'BROKER_MANAGER' || userRole === 'BROKER_ADMIN') {
          // Use the unified owner-ids endpoint so the backend resolves the
          // supervising broker's full agent context for staff roles.
          const url = `${API_ENDPOINTS.brokerTransactionOwnerIds}?activeRole=${encodeURIComponent(userRole)}`;
          const resp = await apiClient.get(url, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const ids = Array.isArray(resp.data) ? resp.data : [];
          setLinkedAgentIds(ids);
        } else {
          // BROKER: use only IDs returned by broker/agents (no self-ID)
          const resp = await apiClient.get(API_ENDPOINTS.brokerAgents, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const agents = Array.isArray(resp.data) ? resp.data : (resp.data?.data || resp.data?.content || []);
          const ids = agents.map(a => a.userId).filter(Boolean);
          setLinkedAgentIds(ids);
        }
      } catch (err) {
        console.error('Error fetching linked agent IDs:', err);
        setLinkedAgentIds([]);
      } finally {
        setAgentsLoaded(true);
      }
    };
    load();
  }, [isRoleReady, isBroker, userRole]);

  // Filter status mappings
  const getStatusParams = (filter) => {
    switch (filter) {
      case 'prospect':
        return 'status=PENDING';
      case 'active':
        return 'status=ACTIVE&status=UNDER_CONTRACT&status=CLOSING';
      case 'fellThrough':
        return 'status=CANCELLED';
      case 'closed':
        return 'status=CLOSED';
      default:
        return 'status=PENDING';
    }
  };

  // Function to get Firebase token - waits for auth to be ready
  const getFirebaseToken = () => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  };

  // Function to fetch transactions from API
  const fetchTransactions = async (filter, pageIndex = 0) => {
    setIsLoading(true);
    setFetchError(false);
    try {
      const firebaseToken = await getFirebaseToken();
      const statusParams = getStatusParams(filter);
      const pageParam = `&page=${pageIndex}&size=${PAGE_SIZE}`;
      let url;
      if (isBrokerage) {
        // BROKERAGE role uses its own endpoint — does the brokerage→broker→agent fan-out server-side
        url = `${API_ENDPOINTS.brokerageTransactions}?${statusParams}&sortBy=createdAt&sortOrder=desc&viewingRole=BROKERAGE${pageParam}`;
      } else if (isBroker) {
        const agentParams = linkedAgentIds.map(id => `agentIds=${encodeURIComponent(id)}`).join('&');
        url = `${API_ENDPOINTS.transactions}?${statusParams}&sortBy=createdAt&sortOrder=desc${agentParams ? `&${agentParams}` : ''}${pageParam}`;
      } else {
        url = `${API_ENDPOINTS.transactions}?${statusParams}&sortBy=createdAt&sortOrder=desc&viewingRole=${userRole}${pageParam}`;
      }

      console.log('Fetching transactions:', url);

      const response = await apiClient.get(url, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Transactions fetched:', response.data);
      // Handle different response formats exhaustively
      const data = response.data;
      let transactionList = [];

      if (Array.isArray(data)) {
        // Format: [ {...}, {...} ]
        transactionList = data;
      } else if (data?.content && Array.isArray(data.content)) {
        // Format: { content: [...], totalElements: N }  (Spring Boot Page)
        transactionList = data.content;
      } else if (data?.data && Array.isArray(data.data)) {
        // Format: { data: [...] }
        transactionList = data.data;
      } else if (data?.data?.content && Array.isArray(data.data.content)) {
        // Format: { data: { content: [...], totalElements: N } }  (nested Spring Boot Page)
        transactionList = data.data.content;
      } else if (data?.transactions && Array.isArray(data.transactions)) {
        // Format: { transactions: [...] }
        transactionList = data.transactions;
      } else if (data?.items && Array.isArray(data.items)) {
        // Format: { items: [...] }
        transactionList = data.items;
      } else if (data?.result && Array.isArray(data.result)) {
        // Format: { result: [...] }
        transactionList = data.result;
      } else if (data?.results && Array.isArray(data.results)) {
        // Format: { results: [...] }
        transactionList = data.results;
      } else if (data && typeof data === 'object') {
        // Fallback: find first array value in the response object
        const firstArray = Object.values(data).find(v => Array.isArray(v));
        if (firstArray) transactionList = firstArray;
      }

      console.log('Parsed transaction list length:', transactionList.length, '| raw format keys:', data && typeof data === 'object' ? Object.keys(data) : typeof data);
      setTransactions(transactionList);
      // Backend returns Spring-style PagedResponse: { content, page, size, totalElements, totalPages }.
      // Older shapes (bare array, { data: [] }) don't carry pagination; default to a single page.
      const te = Number.isFinite(data?.totalElements) ? data.totalElements : transactionList.length;
      const tp = Number.isFinite(data?.totalPages)
        ? data.totalPages
        : Math.max(1, Math.ceil(te / PAGE_SIZE));
      setTotalElements(te);
      setTotalPages(tp);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
      setTotalElements(0);
      setTotalPages(0);
      setFetchError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to page 0 whenever the tab changes so we don't land on an out-of-range
  // page (e.g. viewing page 3 of Prospect then switching to a 1-page Closed tab).
  useEffect(() => {
    setPage(0);
  }, [activeFilter]);

  // Fetch transactions when filter or page changes (wait for role and agent IDs to be ready first)
  useEffect(() => {
    if (isRoleReady && agentsLoaded) {
      fetchTransactions(activeFilter, page);
    }
  }, [activeFilter, page, isRoleReady, agentsLoaded]);

  // Helper function to get phase badge class
  const getPhaseClass = (status) => {
    const phaseClasses = {
      'PENDING': 'badge-prospect',
      'ACTIVE': 'badge-inspection',
      'UNDER_CONTRACT': 'badge-offer',
      'CLOSING': 'badge-escrow',
      'CANCELLED': 'badge-fell-through',
      'CLOSED': 'badge-closed'
    };
    return phaseClasses[status] || 'badge-prospect';
  };

  // Helper function to get status indicator
  const getStatusIndicator = (status) => {
    const indicators = {
      'PENDING': { class: 'pending', icon: '○' },
      'ACTIVE': { class: 'info', icon: '⏱️' },
      'UNDER_CONTRACT': { class: 'warning', icon: '⚠️' },
      'CLOSING': { class: 'success', icon: '✓' },
      'CANCELLED': { class: 'warning', icon: '✕' },
      'CLOSED': { class: 'success', icon: '✓' }
    };
    return indicators[status] || { class: 'pending', icon: '○' };
  };

  // Helper function to format status text
  const formatStatus = (status) => {
    const statusText = {
      'PENDING': 'Prospect',
      'ACTIVE': 'Active',
      'UNDER_CONTRACT': 'Under Contract',
      'CLOSING': 'Closing',
      'CANCELLED': 'Fell Through',
      'CLOSED': 'Closed'
    };
    return statusText[status] || status;
  };

  // Helper function to format a plain calendar date (e.g. expectedClosingDate,
  // a LocalDate with no time-of-day/zone — must NOT go through timezone
  // conversion, which could shift it onto the wrong day).
  const formatDate = formatPlainDate;

  // createdAt is a real instant (saved in UTC by the backend, see
  // TransactionRepository.save) serialized without a 'Z' marker — render it
  // in the viewer's own profile timezone, same treatment as "Add to Timeline".
  const formatCreatedAt = (dateString) => formatUtcNaiveInstantForViewer(dateString).dateLabel;

  // The list is ordered by latest activity (lastActivityAt, also a UTC instant), so show
  // it when present; transactions with no recorded activity yet keep the "Created:" label.
  const formatActivityOrCreated = (t) => (t.lastActivityAt
    ? `Last activity: ${formatCreatedAt(t.lastActivityAt)}`
    : `Created: ${formatCreatedAt(t.createdAt)}`);

  // Map API response to display format
  const currentTransactions = transactions.map((t) => {
    const statusIndicator = getStatusIndicator(t.status);
    return {
      id: t.id,
      address: t.propertyAddress || 'No address',
      city: t.propertyCity || '',
      state: t.propertyState || '',
      clientName: t.transactionName || t.dealName || 'Transaction',
      actionType: t.side,
      status: statusIndicator.class,
      statusIcon: statusIndicator.icon,
      phase: formatStatus(t.status),
      phaseClass: getPhaseClass(t.status),
      meta1: t.listPrice ? `$${t.listPrice.toLocaleString()}` : (t.purchasePrice ? `$${t.purchasePrice.toLocaleString()}` : ''),
      meta2: '',
      date: t.expectedClosingDate ? `Close: ${formatDate(t.expectedClosingDate)}` : formatActivityOrCreated(t),
      agentName: t.agentName || '',
      currentMilestoneName: t.currentMilestoneName || '',
    };
  });

  // Client-side search filter — for broker/brokerage: address, name, and agent name
  const filteredTransactions = searchQuery.trim()
    ? currentTransactions.filter((t) => {
        const q = searchQuery.toLowerCase();
        return (
          t.address.toLowerCase().includes(q) ||
          t.clientName.toLowerCase().includes(q) ||
          (t.agentName && t.agentName.toLowerCase().includes(q))
        );
      })
    : currentTransactions;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleConnectDocusign = async () => {
    if (isConnectingDocusign) return;
    setIsConnectingDocusign(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignConnect, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      if (response.data?.connected === true) {
        return;
      }
      const redirectUrl = response.data?.url || response.data?.redirectUrl || response.data?.authUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        alert('DocuSign connection initiated. Please check your account.');
      }
    } catch (error) {
      console.error('Error connecting DocuSign:', error);
      alert('Failed to connect DocuSign. Please try again.');
    } finally {
      setIsConnectingDocusign(false);
    }
  };

  // DocuSign readiness gate — single-call status check before creating a transaction.
  // Every non-ready state still lets the user through via "Continue without DocuSign";
  // the backend is the final enforcement point. Dev-only bypass: skip the whole check and
  // navigate straight through (see isDevEnvironment above) — stg/prod keep this gate as-is.
  const handleCreateTransactionClick = async (destination) => {
    if (isDevEnvironment) {
      setShowDropdown(false);
      navigate(destination);
      return;
    }
    if (dsChecking) return;
    setShowDropdown(false);
    if (!canSeeDocuSign) {
      navigate(destination);
      return;
    }
    setDsChecking(destination);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignOidcStatus, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const data = response.data || {};
      const connected = data.connected === true;
      const inviteStatus = (data.inviteStatus || '').toLowerCase();
      // Mirrors mobile's hasFormAccess(): also treats a license-state default
      // provider (e.g. CAR for CA) as ready even if provisioned isn't explicitly true.
      // formProviders === null/undefined means the Rooms API was unreachable —
      // mirror mobile's useDocuSignReadiness and treat that as "unknown" (null)
      // rather than "no provider", so the gate fails open instead of blocking.
      let licenseState;
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        licenseState = u.licenseState || u.stateCode || u.state;
      } catch { /* ignore parse errors */ }
      let hasProvisionedProvider = data.formProviders == null
        ? null
        : hasFormAccess(data.formProviders, licenseState);

      // Fallback: some states (e.g. Colorado/CREC) have no form-provider integration
      // on this account at all — their forms are only exposed as a form library.
      // Only check this when the provider check definitively failed (not "unknown").
      if (hasProvisionedProvider === false) {
        const hasLibrary = await hasStateFormLibrary(licenseState, apiClient, API_ENDPOINTS, firebaseToken);
        if (hasLibrary) hasProvisionedProvider = true;
      }

      if (connected && inviteStatus === 'active' && hasProvisionedProvider !== false) {
        // Ready (or unknown, which fails open) — no popup, go straight to the creation page
        navigate(destination);
        return;
      }

      setDsModalDestination(destination);
      setDsModalRoomsUserId(data.roomsUserId || null);
      setDsResendSuccess(false);

      if (!connected) {
        setDsModal('not_connected');
      } else if (inviteStatus === 'pending') {
        setDsModal('pending');
      } else if (inviteStatus === 'active' && hasProvisionedProvider === false) {
        setDsModal('no_forms');
      } else {
        // Unrecognized invite status — allow through, backend will enforce
        navigate(destination);
      }
    } catch (err) {
      console.error('DocuSign status check failed:', err);
      // Allow navigation on unexpected error — backend will enforce
      navigate(destination);
    } finally {
      setDsChecking(null);
    }
  };

  const closeDsModal = () => {
    setDsModal(null);
    setDsResendSuccess(false);
  };

  const handleContinueWithoutDocusign = () => {
    const destination = dsModalDestination;
    closeDsModal();
    if (destination) navigate(destination);
  };

  const handleResendDsInvite = async () => {
    if (!dsModalRoomsUserId || dsResendingInvite) return;
    setDsResendingInvite(true);
    setDsResendSuccess(false);
    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.post(API_ENDPOINTS.docusignRoomsReinvite(dsModalRoomsUserId), {}, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      setDsResendSuccess(true);
    } catch (err) {
      console.error('Resend invite failed:', err);
      alert('Failed to resend invitation. Please try again.');
    } finally {
      setDsResendingInvite(false);
    }
  };

  // Add Form Provider — opens DocuSign form provider setup URL from the readiness gate modal
  const handleGateAddFormProvider = async () => {
    if (dsAddingFormProvider) return;
    setDsAddingFormProvider(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.docusignFormProviderSetupUrl, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const url = response.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Setup URL not available. Please try again.');
      }
    } catch (err) {
      console.error('Failed to get form provider setup URL:', err);
      alert('Failed to open form provider setup. Please try again.');
    } finally {
      setDsAddingFormProvider(false);
    }
  };

  // Connect DocuSign from the readiness gate modal — reuses the same connect flow as the status banner
  const handleGateConnectDocusign = async () => {
    await handleConnectDocusign();
    closeDsModal();
  };

  // DocuSign readiness gate modal content — mirrors mobile app's DocuSignReadinessModal variants
  const DS_GATE_CONFIG = {
    not_connected: {
      icon: 'link-off',
      title: 'DocuSign Not Connected',
      body: 'DocuSign helps you e-sign forms as part of the transaction. Connect now, or continue without it and connect later from Connected Services.',
      primaryLabel: 'Connect DocuSign',
      primaryLoading: isConnectingDocusign,
      onPrimary: handleGateConnectDocusign,
    },
    pending: {
      icon: 'clock',
      title: 'Invitation Pending',
      body: 'Your DocuSign invitation is still pending. Accept it from your email to unlock e-signing, or continue without DocuSign for now.',
      primaryLabel: 'Resend Invite',
      primaryLoading: dsResendingInvite,
      onPrimary: handleResendDsInvite,
    },
    no_forms: {
      icon: 'file-alert',
      title: 'No Form Provider',
      body: "You don't have any form providers set up. Add one in Connected Services to load state-specific forms, or continue without one for now.",
      primaryLabel: 'Add Form Provider',
      primaryLoading: dsAddingFormProvider,
      onPrimary: handleGateAddFormProvider,
    },
  };
  const dsGateConfig = dsModal ? DS_GATE_CONFIG[dsModal] : null;

  const handleCreateListing = () => handleCreateTransactionClick('/listing-transaction');

  const handleCreatePurchase = () => handleCreateTransactionClick('/purchase-transaction');

  const handleTransactionClick = (transaction) => {
    // Navigate to transaction detail page
    navigate(`/transaction/${transaction.id}`);
  };

  return (
    <div className="trans-page-container">

      <div className="trans-main-content">
      <div className="trans-content-box">
        {/* Page Header */}
        <div className="trans-page-header">
          {canUseTemplates ? (
            <div className="trans-view-toggle" role="tablist" aria-label="Transactions view">
              <button
                type="button"
                role="tab"
                aria-selected={!isTemplatesView}
                className={`trans-view-toggle-btn ${!isTemplatesView ? 'active' : ''}`}
                onClick={() => setView('transactions')}
              >
                Transactions
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isTemplatesView}
                className={`trans-view-toggle-btn ${isTemplatesView ? 'active' : ''}`}
                onClick={() => setView('templates')}
              >
                Templates
              </button>
            </div>
          ) : (
            <h1 className="trans-page-title">Transactions</h1>
          )}

          {(userRole === 'AGENT' || userRole === 'TRANSACTION_COORDINATOR') && (
            <div className="trans-header-actions-group">
              <div className="trans-dropdown-wrapper" ref={dropdownRef}>
                <button className="trans-action-button-primary" onClick={toggleDropdown}>
                  <span className="trans-btn-plus-icon">+</span>
                  <span className="trans-btn-label">New Transaction</span>
                  <span className={`trans-dropdown-arrow ${showDropdown ? 'open' : ''}`}>▼</span>
                </button>

                {showDropdown && (
                  <div className="trans-dropdown-menu">
                    <button
                      className="trans-dropdown-item"
                      onClick={handleCreateListing}
                      disabled={!!dsChecking}
                    >
                      <span className="trans-dropdown-icon">
                        <img src={listingIcon} alt="Listing" />
                      </span>
                      <div className="trans-dropdown-item-text">
                        <span className="trans-dropdown-item-title">
                          {dsChecking === '/listing-transaction' ? 'Checking DocuSign...' : 'Listing Transaction'}
                        </span>
                      </div>
                    </button>
                    <button
                      className="trans-dropdown-item"
                      onClick={handleCreatePurchase}
                      disabled={!!dsChecking}
                    >
                      <span className="trans-dropdown-icon">
                        <img src={keyIcon} alt="Purchase" />
                      </span>
                      <div className="trans-dropdown-item-text">
                        <span className="trans-dropdown-item-title">
                          {dsChecking === '/purchase-transaction' ? 'Checking DocuSign...' : 'Buying Transaction'}
                        </span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {isTemplatesView ? (
          <TransactionTemplatesView agentState={_txnListAgentState} />
        ) : (
        <>
        {/* Filter Tabs */}
        <div className="trans-filter-tabs">
          {!isBrokerOrBrokerage && (
            <button
              className={`trans-filter-tab ${activeFilter === 'prospect' ? 'active' : ''}`}
              onClick={() => { setActiveFilter('prospect'); setSearchQuery(''); }}
            >
              Prospect
            </button>
          )}
          <button
            className={`trans-filter-tab ${activeFilter === 'active' ? 'active' : ''}`}
            onClick={() => { setActiveFilter('active'); setSearchQuery(''); }}
          >
            Active
          </button>
          <button
            className={`trans-filter-tab ${activeFilter === 'fellThrough' ? 'active' : ''}`}
            onClick={() => { setActiveFilter('fellThrough'); setSearchQuery(''); }}
          >
            Fell Through
          </button>
          <button
            className={`trans-filter-tab ${activeFilter === 'closed' ? 'active' : ''}`}
            onClick={() => { setActiveFilter('closed'); setSearchQuery(''); }}
          >
            Closed
          </button>
        </div>

        {/* Search bar — broker/brokerage only */}
        {isBrokerOrBrokerage && (
          <div className="trans-search-bar-wrap">
            <svg className="trans-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="trans-search-bar"
              type="text"
              placeholder="Search by address, name, or agent…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="trans-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">✕</button>
            )}
          </div>
        )}

        {/* Transactions Grid */}
        {(!isRoleReady || isLoading) ? (
          <div className="trans-grid">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="trans-skeleton-card">
                <div className="trans-skeleton-header">
                  <div className="trans-skeleton-icon" />
                  <div className="trans-skeleton-lines">
                    <div className="trans-skeleton-line trans-skeleton-line--title" />
                    <div className="trans-skeleton-line trans-skeleton-line--sub" />
                  </div>
                </div>
                <div className="trans-skeleton-line trans-skeleton-line--badge" />
                <div className="trans-skeleton-footer">
                  <div className="trans-skeleton-line trans-skeleton-line--date" />
                </div>
              </div>
            ))}
          </div>
        ) : fetchError ? (
          <div className="trans-empty-state">
            <div className="trans-empty-icon trans-empty-icon--error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2.5"/></svg>
            </div>
            <h3 className="trans-empty-title">Failed to Load</h3>
            <p className="trans-empty-subtitle">
              There was a problem fetching your transactions. Please try again.
            </p>
            <button className="trans-retry-btn" onClick={() => fetchTransactions(activeFilter, page)}>
              Try Again
            </button>
          </div>
        ) : filteredTransactions.length > 0 ? (
          <>
            <div className="trans-results-count">
              {searchQuery
                ? `${filteredTransactions.length} transaction${filteredTransactions.length !== 1 ? 's' : ''} for "${searchQuery}"`
                : `Showing ${filteredTransactions.length} of ${totalElements} transaction${totalElements !== 1 ? 's' : ''}`}
            </div>
            <div className="trans-grid">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className={`trans-card trans-card--${transaction.phaseClass}`}
                  onClick={() => handleTransactionClick(transaction)}
                >
                  <div className="trans-card-header">
                    <div className="trans-property-icon">
                      {(transaction.actionType || '').toUpperCase().includes('LIST') || (transaction.actionType || '').toUpperCase() === 'SALE' ? (
                        <img src={listingIcon} alt="Listing" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                      ) : (transaction.actionType || '').toUpperCase().includes('BUY') || (transaction.actionType || '').toUpperCase() === 'PURCHASE' ? (
                        <img src={keyIcon} alt="Purchase" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      )}
                    </div>
                    <div className="trans-card-info">
                      <div className="trans-property-address">{transaction.clientName}</div>
                      <div className="trans-action-type">{transaction.actionType}</div>
                      {isBrokerOrBrokerage && transaction.agentName && (
                        <div className="trans-agent-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                          </svg>
                          {transaction.agentName}
                        </div>
                      )}
                    </div>
                    {transaction.currentMilestoneName && (
                      <span className="trans-milestone-badge">{transaction.currentMilestoneName}</span>
                    )}
                  </div>

                  <div className="trans-card-badges">
                    {transaction.meta1 && <span className="trans-meta-info">{transaction.meta1}</span>}
                  </div>

                  <div className="trans-card-meta">
                    <span className="trans-meta-info">{transaction.date}</span>
                    <span className="trans-arrow-icon">→</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination — hidden while searching (search is client-side over the current page). */}
            {!searchQuery && totalPages > 1 && (
              <div className="trans-pagination">
                <button
                  className="trans-pagination-btn"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || isLoading}
                >
                  ← Previous
                </button>
                <span className="trans-pagination-info">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  className="trans-pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || isLoading}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="trans-empty-state">
            <div className="trans-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
            </div>
            <h3 className="trans-empty-title">
              {searchQuery ? 'No Results' : 'No Transactions'}
            </h3>
            <p className="trans-empty-subtitle">
              {searchQuery
                ? `No transactions match "${searchQuery}". Try adjusting your search.`
                : isBrokerOrBrokerage
                  ? 'No transactions found for your agents in this category.'
                  : "You don't have any transactions in this category yet."}
            </p>
            {searchQuery && (
              <button className="trans-retry-btn" onClick={() => setSearchQuery('')}>
                Clear Search
              </button>
            )}
          </div>
        )}
        </>
        )}
      </div>
      </div>

      {/* DocuSign Readiness Gate Modal — shown before Listing/Buying Transaction when
          DocuSign isn't fully set up. Mirrors mobile app's DocuSignReadinessModal and
          DashboardPage's ds-gate modal: icon badge, title, body, then a stacked
          filled-primary / outlined-secondary / plain-text-cancel button pattern. */}
      {dsModal && dsGateConfig && (
        <div className="trans-ds-gate-overlay" onClick={closeDsModal}>
          <div className="trans-ds-gate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trans-ds-gate-icon-badge">
              {dsGateConfig.icon === 'link-off' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  <line x1="2" y1="2" x2="22" y2="22"/>
                </svg>
              )}
              {dsGateConfig.icon === 'clock' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              )}
              {dsGateConfig.icon === 'file-alert' && (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#381F83" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="3"/>
                  <line x1="12" y1="12" x2="12" y2="15"/>
                </svg>
              )}
            </div>

            <h3 className="trans-ds-gate-title">{dsGateConfig.title}</h3>
            <p className="trans-ds-gate-message">{dsGateConfig.body}</p>
            {dsModal === 'pending' && dsResendSuccess && (
              <p className="trans-ds-gate-success">Invitation resent successfully.</p>
            )}

            <button className="trans-ds-gate-btn-secondary" onClick={handleContinueWithoutDocusign}>
              Continue without DocuSign
            </button>

            <div className="trans-ds-gate-actions">
              <button className="trans-ds-gate-btn-cancel" onClick={closeDsModal}>
                Cancel
              </button>
              <button
                className="trans-ds-gate-btn-primary"
                onClick={dsGateConfig.onPrimary}
                disabled={dsGateConfig.primaryLoading}
              >
                {dsGateConfig.primaryLoading ? (
                  <span className="trans-ds-gate-spinner" />
                ) : (
                  dsGateConfig.primaryLabel
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsPage;
